import { useCallback, useEffect, useState } from "react";
import {
  fetchSalesDailyChecklist,
  salesChecklistLocalCalendarDay,
  salesChecklistLocalDayBounds,
  toggleSalesDailyChecklistItem,
  type SalesDailyChecklistItem,
} from "@/lib/salesDailyChecklistClient";
import { useUserCapabilities } from "@/hooks/useUserCapabilities";
import {
  adjustChecklistFractionForCapability,
  filterChecklistItemsForCapability,
} from "./salesDailyChecklistFilter";
import { resolveChecklistAllDone } from "@/lib/messages/salesChecklistAckBlocking";

export function useSalesDailyChecklistWidget(scopedOrgId: string | null) {
  // RP-2b-i: resolve the user's `is_ads_runner` capability so the
  // widget can strip the ads-runner item for non-ads-runners (Dean's
  // owner-only complaint in `firefly_role_context_presentation.md`
  // §7 L1). The capability is server-blind in this slice — the API
  // call still returns the full org-policy checklist; the client
  // hides the ads piece in render + recomputes the fraction.
  const hasCapability = useUserCapabilities();
  const isAdsRunner = hasCapability("is_ads_runner");
  const [expanded, setExpanded] = useState(false);
  const [items, setItems] = useState<SalesDailyChecklistItem[]>([]);
  const [fraction, setFraction] = useState<{ done: number; total: number } | null>(null);
  const [allDone, setAllDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<SalesDailyChecklistItem | null>(null);
  const [orgManagerNudge, setOrgManagerNudge] = useState<{ title: string; body: string; sent_at: string } | null>(null);
  const [pendingRequiredAckCount, setPendingRequiredAckCount] = useState(0);
  const [ackBlockingEnabled, setAckBlockingEnabled] = useState(false);

  const load = useCallback(async () => {
    if (!scopedOrgId) return;
    setLoading(true);
    setError(null);
    const day = salesChecklistLocalCalendarDay();
    const bounds = salesChecklistLocalDayBounds();
    const r = await fetchSalesDailyChecklist({
      day,
      published_after: bounds.published_after,
      published_before: bounds.published_before,
    });
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      setItems([]);
      setFraction(null);
      setAllDone(false);
      setOrgManagerNudge(null);
      setPendingRequiredAckCount(0);
      setAckBlockingEnabled(false);
      return;
    }
    // Apply the capability filter before storing so every downstream
    // consumer (rendering, fraction display, all-done banner) reads
    // a single coherent view of "what this user is responsible for."
    // The pure helpers are reference-equal-returning when
    // `isAdsRunner === true`, so ads-runners see no behavior change.
    const rawItems = r.data.items ?? [];
    const filteredItems = filterChecklistItemsForCapability(
      rawItems,
      isAdsRunner,
    );
    setItems(filteredItems as SalesDailyChecklistItem[]);
    const adjustedFraction = adjustChecklistFractionForCapability(
      r.data.fraction,
      rawItems,
      isAdsRunner,
    );
    setFraction(adjustedFraction);
    const ackBlocking = r.data.checklist_meta?.ack_blocking_enabled === true;
    const ackCount = r.data.checklist_meta?.pending_required_ack_count;
    const pendingAcks = typeof ackCount === "number" && ackCount > 0 ? ackCount : 0;
    setAckBlockingEnabled(ackBlocking);
    setPendingRequiredAckCount(pendingAcks);
    setAllDone(
      resolveChecklistAllDone({
        fraction: adjustedFraction,
        ackBlockingEnabled: ackBlocking,
        pendingRequiredAckCount: pendingAcks,
      })
    );
    const nudge = r.data.checklist_meta?.org_manager_nudge;
    if (nudge && typeof nudge.title === "string" && typeof nudge.sent_at === "string") {
      setOrgManagerNudge({
        title: nudge.title,
        body: typeof nudge.body === "string" ? nudge.body : "",
        sent_at: nudge.sent_at,
      });
    } else {
      setOrgManagerNudge(null);
    }
    // `isAdsRunner` is a dep because flipping it (e.g. user toggles
    // off in Settings — RP-2b-ii) must re-derive the filtered view
    // even though the server response hasn't changed.
  }, [scopedOrgId, isAdsRunner]);

  useEffect(() => {
    void load();
  }, [load]);

  const onToggle = async (item: SalesDailyChecklistItem, nextDone: boolean) => {
    if (!scopedOrgId || item.kind !== "manual") return;
    setTogglingId(item.id);
    const r = await toggleSalesDailyChecklistItem({
      day: salesChecklistLocalCalendarDay(),
      item_key: item.id,
      done: nextDone,
    });
    setTogglingId(null);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    await load();
  };

  return {
    expanded,
    setExpanded,
    items,
    fraction,
    allDone,
    loading,
    error,
    togglingId,
    pendingConfirm,
    setPendingConfirm,
    orgManagerNudge,
    pendingRequiredAckCount,
    ackBlockingEnabled,
    load,
    onToggle,
  };
}
